import React, { useEffect, useRef, useState } from 'react';
import { Shield, Truck, Headphones, BadgeCheck } from 'lucide-react';

const statsData = [
  { value: "10+", label: "Anos de Experiência" },
  { value: "200+", label: "Clientes Ativos" },
  { value: "1K+", label: "Produtos em Catálogo" }
];

const aboutText = "Há mais de 10 anos, a Smart Solutions Group se destaca como o elo de confiança das principais empresas do Brasil. Nossa missão é fornecer soluções completas, combinando um portfólio premium com atendimento personalizado e logística eficiente. Somos reconhecidos pela excelência em cada entrega e pelo compromisso inegociável com o sucesso operacional de nossos clientes";

const AboutSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="sobre"
      ref={sectionRef}
      className="relative py-24 sm:py-32 bg-black overflow-hidden"
    >
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent" />
        <div className="absolute top-1/2 -right-64 w-[500px] h-[500px] bg-amber-900/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 -left-64 w-[400px] h-[400px] bg-slate-800/30 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <div
          className={`grid grid-cols-1 sm:grid-cols-3 gap-8 mb-20 sm:mb-28 transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
        >
          {statsData.map((stat, index) => (
            <div
              key={stat.label}
              className="text-center p-6 rounded-xl bg-slate-900/30 border border-slate-800/30 transition-all duration-500 hover:border-slate-700/50"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2">
                <span className="bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent">
                  {stat.value}
                </span>
              </div>
              <div className="text-slate-400 text-sm sm:text-base">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className={`grid lg:grid-cols-2 gap-16 lg:gap-24 items-center ${isVisible ? 'is-visible' : ''}`}>
          <div>
            <span className="inline-block text-amber-500 text-sm font-medium tracking-widest uppercase mb-4">
              Sobre Nós
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight overflow-hidden">
              <span className="block split-text-inner">Sua Parceira Estratégica em Suprimentos</span>
            </h2>
            <p className="text-slate-400 text-lg leading-relaxed mb-8 overflow-hidden">
              <span className="block split-text-inner">
                {aboutText}
              </span>
            </p>
            <div className="flex items-center gap-4 pt-4">
              <div className="w-12 h-px bg-gradient-to-r from-amber-500 to-transparent" />
              <span className="text-slate-500 text-sm italic">Compromisso com a excelência desde 2010</span>
            </div>
          </div>

          <div
            className={`grid sm:grid-cols-2 gap-6 transition-all duration-1000 ease-out delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
              }`}
          >
            <div className="group p-6 rounded-xl bg-slate-900/40 border border-slate-800/40 transition-all duration-500 hover:bg-slate-900/60 hover:border-amber-600/20 hover:-translate-y-1">
              <div className="w-12 h-12 rounded-lg bg-amber-600/10 border border-amber-600/20 flex items-center justify-center mb-4 transition-all duration-300 group-hover:bg-amber-600/20">
                <Shield className="w-6 h-6 text-amber-500" />
              </div>
              <h3 className="text-white font-semibold mb-2">Qualidade Garantida</h3>
              <p className="text-slate-400 text-sm leading-relaxed">Produtos certificados e de marcas reconhecidas mundialmente.</p>
            </div>

            <div className="group p-6 rounded-xl bg-slate-900/40 border border-slate-800/40 transition-all duration-500 hover:bg-slate-900/60 hover:border-amber-600/20 hover:-translate-y-1">
              <div className="w-12 h-12 rounded-lg bg-amber-600/10 border border-amber-600/20 flex items-center justify-center mb-4 transition-all duration-300 group-hover:bg-amber-600/20">
                <Truck className="w-6 h-6 text-amber-500" />
              </div>
              <h3 className="text-white font-semibold mb-2">Logística Eficiente</h3>
              <p className="text-slate-400 text-sm leading-relaxed">Entregas rápidas para todo o Brasil.</p>
            </div>

            <div className="group p-6 rounded-xl bg-slate-900/40 border border-slate-800/40 transition-all duration-500 hover:bg-slate-900/60 hover:border-amber-600/20 hover:-translate-y-1">
              <div className="w-12 h-12 rounded-lg bg-amber-600/10 border border-amber-600/20 flex items-center justify-center mb-4 transition-all duration-300 group-hover:bg-amber-600/20">
                <Headphones className="w-6 h-6 text-amber-500" />
              </div>
              <h3 className="text-white font-semibold mb-2">Suporte Especializado</h3>
              <p className="text-slate-400 text-sm leading-relaxed">Equipe dedicada para atender suas necessidades específicas.</p>
            </div>

            <div className="group p-6 rounded-xl bg-slate-900/40 border border-slate-800/40 transition-all duration-500 hover:bg-slate-900/60 hover:border-amber-600/20 hover:-translate-y-1">
              <div className="w-12 h-12 rounded-lg bg-amber-600/10 border border-amber-600/20 flex items-center justify-center mb-4 transition-all duration-300 group-hover:bg-amber-600/20">
                <BadgeCheck className="w-6 h-6 text-amber-500" />
              </div>
              <h3 className="text-white font-semibold mb-2">Parceria de Confiança</h3>
              <p className="text-slate-400 text-sm leading-relaxed">Relacionamentos duradouros construídos com transparência.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
